// Template editor screen — Block picker + ordered Block instance list +
// per-block options form + Duplicate / Delete actions. Per
// spec/build-plan.md P-031 row + tech-arch.md §6 + §7 + §2.4. First
// reachable mount of `<Form>` (P-025-P-028) in the app shell.
//
// **Param-id validation.** `useRoute().params.id` is a URL-hash string;
// validate against `templates[]` before reading. Two paths: (a) match →
// render editor; (b) no match → render `<TemplateNotFound />` with link
// back to `#/templates`.
//
// **Save = implicit (D-083 store auto-persist).** Every store write
// flows through `setState` and persists with a 500ms debounce. No
// visible Save button at V1; "Saved" indicator deferred to P-035 polish.
//
// **Duplicate / Delete.** Duplicate reuses the `duplicateTemplate`
// factory (P-024 / D-086) and navigates to the new editor route. Delete
// uses `<ConfirmModal>` (D-086) → store remove → navigate back to the
// list. Block-level reorder/remove fire inline (no confirm; reversible
// by clicking + Add block / ↑ / ↓).

import { useEffect, useRef, useState } from 'preact/hooks'

import { blocks, type BlockId } from '../../registry/blocks'
import { duplicateAndOpenTemplate, updateTemplate } from '../../store/factories'
import type { Template } from '../../store/schemas'
import { store } from '../../store/store'
import { secondaryButtonClass } from '../button-classes'
import { ConfirmModal } from '../confirm-modal'
import { TEMPLATE_NAME_EDIT_PROMPT, TEMPLATE_NAME_PLACEHOLDER } from '../constants'
import { useRoute } from '../hooks/use-route'
import { useStore } from '../hooks/use-store'
import { BlockInstanceCard } from './block-instance-card'
import { BlockPickerModal } from './block-picker-modal'
import { DirtyAsterisk } from './dirty-asterisk'
import { SavedIndicator } from './saved-indicator'
import {
  addBlockToTemplate,
  moveBlockInTemplate,
  removeBlockFromTemplate,
  setBlockDefaultInputsInTemplate,
  setBlockOptionsInTemplate,
  setTemplateName,
} from './template-editor-helpers'

// P-037.31 / D-139 — Block IDs that surface a "Defaults" section in the
// Template editor for setting per-instance `inputs` defaults. V1 = 3
// Blocks (section-heading + sub-section-heading + text); easy to widen
// later by adding entries to this set.
const BLOCKS_WITH_DEFAULTABLE_INPUTS: ReadonlySet<string> = new Set([
  'section-heading',
  'sub-section-heading',
  'text',
])

function deleteTemplateAndLeave(id: string) {
  store.setState((s) => ({
    ...s,
    templates: s.templates.filter((t) => t.id !== id),
    currentTemplateId: s.currentTemplateId === id ? null : s.currentTemplateId,
  }))
  window.location.hash = '#/templates'
}

function TemplateNotFound({ id }: { id: string }) {
  return (
    <div class="space-y-4">
      <h2 class="m-0 text-lg font-semibold">Template not found</h2>
      <p class="m-0 text-sm text-gray-600">
        No template with id <code class="font-mono">{id}</code>.
      </p>
      <a
        href="#/templates"
        class={`inline-block ${secondaryButtonClass}`}
      >
        ← Back to templates
      </a>
    </div>
  )
}

export function TemplateEditorScreen() {
  const { params } = useRoute()
  const { templates } = useStore()
  const id = params.id ?? ''
  const template = templates.find((t) => t.id === id)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const nameInputRef = useRef<HTMLInputElement | null>(null)

  // Focus + select-all when entering edit mode so staff can immediately type
  // over the existing name (Notion / Linear idiom).
  useEffect(() => {
    if (editingName) {
      nameInputRef.current?.focus()
      nameInputRef.current?.select()
    }
  }, [editingName])

  if (template === undefined) return <TemplateNotFound id={id} />

  const dispatch = (transform: (t: Template) => Template) => {
    updateTemplate(template.id, transform)
  }

  return (
    <div class="space-y-6">
      <header class="flex items-baseline justify-between gap-4">
        <div class="min-w-0">
          <h2 class="m-0 flex items-baseline gap-2 truncate text-lg font-semibold">
            {editingName ? (
              <input
                ref={nameInputRef}
                type="text"
                value={template.name}
                onInput={(e) =>
                  dispatch((t) =>
                    setTemplateName(t, (e.target as HTMLInputElement).value),
                  )
                }
                onBlur={() => setEditingName(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    ;(e.target as HTMLInputElement).blur()
                  }
                }}
                aria-label="Template name"
                placeholder={TEMPLATE_NAME_PLACEHOLDER}
                class="min-w-0 flex-1 truncate rounded border border-ypo-navy/40 bg-white px-1 text-lg font-semibold text-inherit focus:outline-none focus:ring-2 focus:ring-ypo-navy/30"
              />
            ) : (
              <button
                type="button"
                onClick={() => setEditingName(true)}
                aria-label="Rename template"
                class="-mx-1 inline-flex min-w-0 cursor-text items-baseline gap-1.5 truncate rounded px-1 text-left text-inherit hover:bg-gray-100"
              >
                <span
                  class={`min-w-0 truncate ${template.name === '' ? 'italic text-gray-500' : ''}`}
                >
                  {template.name || TEMPLATE_NAME_EDIT_PROMPT}
                </span>
                <span aria-hidden="true" class="shrink-0 text-base text-gray-400">
                  ✏
                </span>
              </button>
            )}
            <DirtyAsterisk />
          </h2>
          <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-gray-500">
            <span>
              {template.blocks.length} block{template.blocks.length === 1 ? '' : 's'}
            </span>
            <SavedIndicator />
          </div>
        </div>
        <div class="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => duplicateAndOpenTemplate(template)}
            class={secondaryButtonClass}
          >
            Duplicate
          </button>
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            class="rounded border border-red-300 bg-white px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      </header>

      {template.blocks.length === 0 ? (
        <div class="rounded border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
          <p class="m-0 text-sm text-gray-600">This template has no blocks yet.</p>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            class="mt-3 rounded bg-ypo-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-ypo-navy-deep"
          >
            Add your first block
          </button>
        </div>
      ) : (
        <>
          <ul class="m-0 list-none space-y-3 p-0">
            {template.blocks.map((instance, idx) => {
              const def = blocks[instance.blockId as BlockId]
              if (def === undefined) {
                return (
                  <li
                    key={instance.instanceId}
                    class="rounded border border-dashed border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
                  >
                    Unknown block id <code class="font-mono">{instance.blockId}</code>.
                  </li>
                )
              }
              const showDefaults = BLOCKS_WITH_DEFAULTABLE_INPUTS.has(instance.blockId)
              return (
                <BlockInstanceCard
                  key={instance.instanceId}
                  instance={instance}
                  def={def}
                  canMoveUp={idx > 0}
                  canMoveDown={idx < template.blocks.length - 1}
                  onMoveUp={() =>
                    dispatch((t) => moveBlockInTemplate(t, instance.instanceId, -1))
                  }
                  onMoveDown={() =>
                    dispatch((t) => moveBlockInTemplate(t, instance.instanceId, +1))
                  }
                  onRemove={() =>
                    dispatch((t) => removeBlockFromTemplate(t, instance.instanceId))
                  }
                  onOptionsChange={(next) =>
                    dispatch((t) => setBlockOptionsInTemplate(t, instance.instanceId, next))
                  }
                  showDefaults={showDefaults}
                  {...(showDefaults
                    ? {
                        onDefaultInputsChange: (next: unknown) =>
                          dispatch((t) =>
                            setBlockDefaultInputsInTemplate(t, instance.instanceId, next),
                          ),
                      }
                    : {})}
                />
              )
            })}
          </ul>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            class="rounded bg-ypo-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-ypo-navy-deep"
          >
            + Add block
          </button>
        </>
      )}

      <BlockPickerModal
        open={pickerOpen}
        onPick={(blockId) => {
          dispatch((t) => addBlockToTemplate(t, blockId))
          setPickerOpen(false)
        }}
        onCancel={() => setPickerOpen(false)}
      />

      <ConfirmModal
        open={deleteOpen}
        title="Delete this Template?"
        message={`"${template.name || TEMPLATE_NAME_PLACEHOLDER}" will be removed. This cannot be undone.`}
        onConfirm={() => {
          setDeleteOpen(false)
          deleteTemplateAndLeave(template.id)
        }}
        onCancel={() => setDeleteOpen(false)}
      />
    </div>
  )
}
